import { createFileRoute } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import { faqPageSchema, type Faq } from "@/data/serviceFaqs";
import img from "@/assets/brands-lifestyle.webp";
import { serviceAreas } from "@/data/serviceAreas";

const URL = "https://apexav.com/services/av-installation";

const faqs: Faq[] = [
  {
    q: "How much does AV installation cost in Denver?",
    a: "A single-room AV installation — TV, soundbar or in-ceiling speakers, sources, and control — typically runs $1,500–$6,000 installed. Multi-room audio/video distribution with a rack, matrix switching, and app control usually lands between $8,000 and $35,000. Dedicated theaters start around $25,000. Every Denver project is quoted fixed-bid before we start.",
  },
  {
    q: "What does an AV installer actually do?",
    a: "We design the system, pull and terminate the cabling, mount displays and speakers, build and wire the equipment rack, program the control system, and calibrate picture and sound. You get one app that works, labeled wiring, and a local team that supports it afterward.",
  },
  {
    q: "Can you install AV in an existing Denver home without tearing up walls?",
    a: "Yes. Retrofit is most of what we do. We fish cable through finished walls, use existing chases and attics, and patch and paint where needed. Where a wire genuinely can't go, we spec reliable wireless gear instead of guessing.",
  },
  {
    q: "Do you handle commercial AV installation too?",
    a: "Yes — conference rooms, restaurants, gyms, and offices across the Denver Metro. That includes displays, distributed audio, microphones, video conferencing, and networked control.",
  },
  {
    q: "Which areas do you cover for AV installation?",
    a: "The full Denver Metro and Front Range: Denver, Cherry Creek, Aurora, Highlands Ranch, Littleton, Lakewood, Centennial, Parker, Castle Rock, Castle Pines, Golden, Boulder, and Evergreen.",
  },
];

const testimonials = [
  { quote: "They handled every piece of our AV — TVs, ceiling speakers, the rack, the network. One app, and it just works.", name: "Chris B.", location: "Littleton" },
  { quote: "Third AV company we tried and the first that actually finished the job cleanly. The rack looks like a showroom.", name: "Andrea T.", location: "Cherry Creek" },
  { quote: "Retrofitted audio into a 1920s house without wrecking the plaster. Impressed.", name: "Sam O.", location: "Wash Park, Denver" },
];

export const Route = createFileRoute("/services/av-installation")({
  head: () => ({
    meta: [
      { title: "AV Installation Denver | Audio Video Installers | Apex AV" },
      {
        name: "description",
        content:
          "AV installation in Denver by factory-certified audio video installers. TVs, in-ceiling audio, equipment racks, video distribution, and control — residential and commercial. Free consultation.",
      },
      { name: "keywords", content: "av installation Denver, audio video installation Denver, AV installers Denver, home av installation Denver, commercial av installation Denver, audio video installer near me Denver" },
      { property: "og:title", content: "AV Installation Denver — Apex AV" },
      { property: "og:description", content: "Factory-certified audio video installation for Denver homes and businesses." },
      { property: "og:image", content: `https://apexav.com${img}` },
      { name: "twitter:image", content: `https://apexav.com${img}` },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "AV Installation Denver — Apex AV" },
      { name: "twitter:description", content: "Audio video installation done right across the Denver Metro." },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "preload", as: "image", href: img, fetchPriority: "high" } as never,
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              ...faqPageSchema(faqs, URL),
              "@id": `${URL}#faq`,
              inLanguage: "en-US",
              about: { "@id": `${URL}#service` },
            },
            {
              "@type": "Service",
              "@id": `${URL}#service`,
              name: "AV Installation",
              alternateName: ["Audio Video Installation", "Home AV Installation"],
              description:
                "Residential and commercial audio video installation in Denver — displays, distributed audio, equipment racks, video distribution, and control programming.",
              url: URL,
              image: `https://apexav.com${img}`,
              provider: { "@id": "https://apexav.com/#organization" },
              serviceType: "Audio Video Installation",
              category: "Home Improvement",
              areaServed: [
                { "@type": "AdministrativeArea", name: "Denver Metro & Front Range, Colorado" },
                ...serviceAreas.map((a) => ({
                  "@type": "City",
                  name: a.name,
                  address: {
                    "@type": "PostalAddress",
                    addressLocality: a.name,
                    addressRegion: "CO",
                    addressCountry: "US",
                  },
                })),
              ],
              audience: { "@type": "Audience", audienceType: "Homeowners and businesses in the Denver metro area" },
              offers: {
                "@type": "Offer",
                url: URL,
                availability: "https://schema.org/InStock",
                priceSpecification: {
                  "@type": "PriceSpecification",
                  price: 1500,
                  minPrice: 1500,
                  priceCurrency: "USD",
                  valueAddedTaxIncluded: false,
                  description:
                    "Single-room AV installations typically run $1,500–$6,000. Whole-home audio/video distribution with an equipment rack and app control generally runs $8,000–$35,000. Every project is quoted fixed-bid after an in-home consultation.",
                },
              },
              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: "AV Installation Services",
                itemListElement: [
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "TV and projector installation with concealed cabling" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "In-ceiling, in-wall, and architectural speaker installation" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Equipment rack design, build, and cable management" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Multi-room video distribution and matrix switching" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Control system programming" } },
                  { "@type": "Offer", itemOffered: { "@type": "Service", name: "Commercial AV for conference rooms, restaurants, offices, and gyms" } },
                ],
              },
              mainEntityOfPage: { "@id": `${URL}#webpage` },
            },
            {
              "@type": "WebPage",
              "@id": `${URL}#webpage`,
              url: URL,
              name: "AV Installation Denver | Audio Video Installers | Apex AV",
              description:
                "AV installation in Denver by factory-certified audio video installers — TVs, in-ceiling audio, equipment racks, video distribution, and control.",
              inLanguage: "en-US",
              isPartOf: { "@id": "https://apexav.com/#website" },
              primaryImageOfPage: { "@type": "ImageObject", url: `https://apexav.com${img}` },
              breadcrumb: { "@id": `${URL}#breadcrumb` },
            },
            {
              "@type": "BreadcrumbList",
              "@id": `${URL}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://apexav.com/" },
                { "@type": "ListItem", position: 2, name: "Services", item: "https://apexav.com/services" },
                { "@type": "ListItem", position: 3, name: "AV Installation Denver", item: URL },
              ],
            },
          ],
        }),
      },
    ],

  }),
  component: () => (
    <ServiceDetail
      topics={["home-theater", "whole-home-audio", "networking", "tv-mounting", "whole-home-video", "commercial"]}
      eyebrow="AV Installation Denver"
      title="AV Installation in Denver"
      intro={
        <>
          Full-scope <strong>AV installation in Denver</strong> — displays, in-ceiling and in-wall audio, equipment racks,
          video distribution, and control programming for homes and businesses across the Denver Metro. Designed, wired,
          calibrated, and supported by the same local crew.
        </>
      }
      image={img}
      imageAlt="Audio video installation in a Denver, Colorado home with in-ceiling speakers and wall-mounted display"
      brands={["sonos", "sony", "lg", "samsung"]}
      bullets={[
        "TV and projector installation with concealed cabling",
        "In-ceiling, in-wall, and architectural speaker installation",
        "Equipment rack design, build, and cable management",
        "Multi-room video distribution and matrix switching",
        "Control system programming — one app for the whole system",
        "Commercial AV: conference rooms, restaurants, offices, gyms",
      ]}
      features={[
        { title: "Engineered, not improvised", desc: "Signal paths, power, and heat planned on paper before a single wire is pulled." },
        { title: "Retrofit specialists", desc: "We fish cable through finished Denver homes — plaster, brick, and 1920s framing included." },
        { title: "Calibrated and documented", desc: "Picture and sound dialed in, wiring labeled, and a system handoff you can actually use." },
      ]}
      startingPrice="$1,500"
      priceNote="Single-room AV installs typically run $1,500–$6,000. Whole-home audio/video distribution with a rack and app control generally runs $8,000–$35,000, always fixed-bid."
      testimonials={testimonials}
      faqs={faqs}
      serviceAreas={serviceAreas.map((a) => ({ slug: a.slug, name: a.name }))}
    />
  ),
});
